import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [ready, user, nav]);

  const load = () => {
    api.adminPosts().then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.deletePost(id);
      toast.success("Post deleted");
      setPosts((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (!ready || !user) return <div className="min-h-screen bg-bone flex items-center justify-center text-stone">Loading…</div>;

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-forest text-bone">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl">Content Dashboard</h1>
            <p className="text-bone/60 text-sm">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/local-guide" target="_blank" className="text-sm flex items-center gap-1 hover:text-amber transition-colors">
              View Site <ExternalLink size={14} />
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="text-sm flex items-center gap-1 hover:text-amber transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif text-3xl text-forest">Posts ({posts.length})</h2>
          <Link
            to="/admin/new"
            data-testid="admin-new-post"
            className="inline-flex items-center gap-2 rounded-full bg-amber text-bone px-6 py-3 text-sm font-medium hover:bg-amber-deep transition-colors"
          >
            <Plus size={16} /> New Post
          </Link>
        </div>

        {loading ? (
          <p className="text-stone">Loading posts…</p>
        ) : (
          <div className="border border-stone/15 rounded-md divide-y divide-stone/10 bg-card">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-post-${p.slug}`}>
                <img src={p.cover_image} alt="" className="w-20 h-14 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-forest truncate">{p.title}</p>
                  <p className="text-xs text-stone">
                    {p.category} ·{" "}
                    <span className={p.status === "published" ? "text-green-700" : "text-amber-deep"}>{p.status}</span>
                  </p>
                </div>
                <Link to={`/admin/edit/${p.id}`} data-testid={`admin-edit-${p.slug}`} className="p-2 text-stone hover:text-amber transition-colors">
                  <Pencil size={18} />
                </Link>
                <button onClick={() => remove(p.id)} data-testid={`admin-delete-${p.slug}`} className="p-2 text-stone hover:text-red-600 transition-colors">
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            {posts.length === 0 && <p className="p-6 text-stone">No posts yet. Create your first one.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
